import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@tanstack/react-query';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, Stack, TextField, Typography
} from '@mui/material';
import { createAdministrative, fetchAdministrative, updateAdministrative } from '../../services/administratives';
import { t } from '../../i18n';

export default function AdministrativeDialog({ actor, target, onClose, onSaved }) {
    const active = useRef(true);
    const lock = useRef(false);
    const [error, setError] = useState(null);
    useEffect(() => { active.current = true; return () => { active.current = false; }; }, []);
    const details = useQuery({
        queryKey: ['clinic-administratives-detail', actor.role.uuid, actor.role.account.uuid, target?.role_uuid],
        queryFn: () => fetchAdministrative(actor, target),
        enabled: Boolean(target), retry: 1, staleTime: Infinity
    });
    const formik = useFormik({
        initialValues: {
            name: details.data?.name || target?.name || '',
            email: target?.email || '',
            phone: details.data?.phone || ''
        },
        enableReinitialize: true,
        validationSchema: Yup.object({
            name: Yup.string().trim().max(80, 'O texto excede o tamanho permitido.').required('Indique o nome.'),
            email: Yup.string().trim().max(256, 'O texto excede o tamanho permitido.')
                .email('Indique um email válido.').required('Indique o email.'),
            phone: Yup.string().trim().max(20, 'O texto excede o tamanho permitido.')
        }),
        onSubmit: async (values) => {
            if (lock.current || (target && !details.data)) return;
            lock.current = true; setError(null);
            try {
                let recovered = false;
                if (target) await updateAdministrative(actor, target, values);
                else ({ recovered } = await createAdministrative(actor, values));
                if (active.current) onSaved(recovered);
            } catch {
                if (active.current) setError('Não foi possível guardar o administrativo. Verifique os dados e tente novamente.');
            } finally { lock.current = false; }
        }
    });
    const field = (name, label, max, extra = {}) => <TextField fullWidth name={name} label={t(label)}
        value={formik.values[name]} onChange={formik.handleChange} onBlur={formik.handleBlur}
        disabled={formik.isSubmitting} inputProps={{ maxLength: max }}
        error={Boolean(formik.touched[name] && formik.errors[name])}
        helperText={formik.touched[name] && t(formik.errors[name])} {...extra} />;
    return <Dialog open fullWidth maxWidth="sm" onClose={() => { if (!formik.isSubmitting) onClose(); }}
        aria-labelledby="clinic-administratives-dialog-title">
        <Box component="form" noValidate onSubmit={formik.handleSubmit}>
            <DialogTitle id="clinic-administratives-dialog-title">{t(target ? 'Editar administrativo' : 'Adicionar administrativo')}</DialogTitle>
            <DialogContent>
                {target && details.isPending ? <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress /></Box>
                    : target && details.isError ? <Alert severity="error" action={<Button onClick={() => details.refetch()}>{t('Tentar novamente')}</Button>}>
                        {t('Não foi possível consultar este acesso.')}
                    </Alert> : <Stack spacing={2.5} sx={{ pt: 1 }}>
                        {error && <Alert severity="error">{t(error)}</Alert>}
                        {field('name', 'Nome', 80, { required: true })}
                        {field('email', 'Email', 256, { required: true, type: 'email', InputProps: { readOnly: Boolean(target) } })}
                        {field('phone', 'Telefone', 20)}
                        <Typography variant="caption" color="text.secondary">
                            {t(target
                                ? 'O nome é partilhado com os restantes perfis desta conta.'
                                : 'Se a conta já existir, este perfil será associado ou reativado. Será enviada uma notificação por email.')}
                        </Typography>
                    </Stack>}
            </DialogContent>
            <DialogActions>
                <Button disabled={formik.isSubmitting} onClick={onClose}>{t('Cancelar')}</Button>
                <Button type="submit" variant="contained" disabled={formik.isSubmitting || Boolean(target && !details.data)}>
                    {t(formik.isSubmitting ? 'A guardar…' : target ? 'Guardar alterações' : 'Adicionar administrativo')}
                </Button>
            </DialogActions>
        </Box>
    </Dialog>;
}
AdministrativeDialog.propTypes = {
    actor: PropTypes.object.isRequired, target: PropTypes.object,
    onClose: PropTypes.func.isRequired, onSaved: PropTypes.func.isRequired
};
